// Component for displaying and managing travel lists
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const TravelLists = () => {
    const [travelLists, setTravelLists] = useState([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [travelListNameSearchTerm, setTravelListNameSearchTerm] = useState('');
    const [clothesSearchTerm, setClothesSearchTerm] = useState('');
    const [expandedClothes, setExpandedClothes] = useState({}); // For tracking expanded state
    const [destinationSearchTerm, setDestinationSearchTerm] = useState('');
    const [departureDateSearchTerm, setDepartureDateSearchTerm] = useState('');
    const [returnDateSearchTerm, setReturnDateSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetchTravelLists(); // Fetch travel lists data on component load
    }, []);

    // Fetch travel lists from the backend
    const fetchTravelLists = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('http://localhost:3000/api/travellists', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTravelLists(response.data);
        } catch (error) {
            console.error('Error fetching travel lists:', error);
        }
    };

    const handleToggleCloth = (travelListId, clothId) => {
        setExpandedClothes((prev) => ({
            ...prev,
            [travelListId]: {
                ...prev[travelListId],
                [clothId]: !prev[travelListId]?.[clothId]  // Toggle visibility for the clicked cloth
            }
        }));
    };

    const handleDelete = async (id) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`http://localhost:3000/api/travellists/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTravelLists(travelLists.filter(travelList => travelList._id !== id)); // Update the list
        } catch (error) {
            console.error('Error deleting travel list:', error);
        }
    };

    const filteredTravelLists = travelLists.filter(travelList => {
        const matchesTravelListName = travelList.list_name.toLowerCase().includes(travelListNameSearchTerm.toLowerCase());
        const matchesDestination = travelList.destination.some(des => des.toLowerCase().includes(destinationSearchTerm.toLowerCase()));
        const matchesDepartureDate = !departureDateSearchTerm || travelList.departure_date?.slice(0, 10) === departureDateSearchTerm;
        const matchesReturnDate = !returnDateSearchTerm || travelList.return_date?.slice(0, 10) === returnDateSearchTerm;
        const matchesClothingItems = travelList.clothing_items && travelList.clothing_items.some(cloth => 
            cloth.cloth_name.toLowerCase().includes(clothesSearchTerm.toLowerCase()) ||
            cloth.color.some(col => col.toLowerCase().includes(clothesSearchTerm.toLowerCase())) || 
            cloth.category.some(cat => cat.toLowerCase().includes(clothesSearchTerm.toLowerCase())) ||
            cloth.season.some(sea => sea.toLowerCase().includes(clothesSearchTerm.toLowerCase()))
        );

        return matchesTravelListName && matchesDestination && matchesDepartureDate && matchesReturnDate && matchesClothingItems;
    });

    return (
        <div className="travel-lists-container">
            <h1><strong>𝒯𝓇𝒶𝓋𝑒𝓁 𝐿𝒾𝓈𝓉𝓈 ({travelLists.length})</strong></h1>

            {/* Search Filters Button */}
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="dropdown-btn">
                <i className="fa-solid fa-magnifying-glass"></i> <strong>𝒮𝑒𝒶𝓇𝒸𝒽 𝐹𝒾𝓁𝓉𝑒𝓇𝓈</strong>
            </button>

            {/* Create Travel List Button */}
            <button onClick={() => navigate('/create-travelList')} className="create-travel-list-btn">
                <i className="fa-solid fa-plus"></i> <strong>𝒞𝓇𝑒𝒶𝓉𝑒 𝒯𝓇𝒶𝓋𝑒𝓁 𝐿𝒾𝓈𝓉</strong>
            </button>

            {/* Dropdown Search Filters */}
            {isDropdownOpen && (
                <div className="search-container">
                    <div className="filters-container">
                        <label>
                            <strong>Travel List Name:</strong>            
                            <input
                                type="text"
                                placeholder="Search by travel list name"
                                value={travelListNameSearchTerm}
                                onChange={(e) => setTravelListNameSearchTerm(e.target.value)}
                            />
                        </label>
                        <label>
                            <strong>Destination\-s:</strong>
                            <input
                                type="text"
                                placeholder="e.g. Greece, Corfu (Greece)"
                                value={destinationSearchTerm}
                                onChange={(e) => setDestinationSearchTerm(e.target.value)}
                            />
                        </label>
                        <label>
                            <strong>Departure Date:</strong>
                            <input
                                type="date"
                                value={departureDateSearchTerm}
                                onChange={(e) => setDepartureDateSearchTerm(e.target.value)}
                            />
                        </label>
                        <label>
                            <strong>Return Date:</strong>
                            <input
                                type="date"
                                value={returnDateSearchTerm}
                                onChange={(e) => setReturnDateSearchTerm(e.target.value)}
                            />
                        </label>
                        <label>
                            <strong>Clothes:</strong>
                            <input
                                type="text"
                                placeholder="Search by cloth name, color, category, or season"
                                value={clothesSearchTerm}
                                onChange={(e) => setClothesSearchTerm(e.target.value)}
                            />
                        </label>
                    </div>
                </div>
            )}

            <div className="travel-lists-grid">
                {filteredTravelLists.map((travelList) => (
                    <div key={travelList._id} className="travel-list-item">
                        <h4><strong>{travelList.list_name}</strong></h4>
                        <p><strong>Destination\-s:</strong> {travelList.destination.join(', ')}</p>
                        <p><strong>Departure Date:</strong> {new Date(travelList.departure_date).toLocaleDateString()}</p>
                        <p><strong>Return Date:</strong> {new Date(travelList.return_date).toLocaleDateString()}</p>
                        <p><strong>Description: </strong>{travelList.description}</p>
                        <p><strong>Clothes: </strong></p>
                        <div className="clothing-items">
                            {travelList.clothing_items.map((cloth) => (
                                <div key={cloth._id} className="clothing-item">
                                    <p
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggleCloth(travelList._id, cloth._id); // Pass both outfit and cloth ID
                                        }}
                                        className="cloth-name"
                                    >
                                        {cloth.cloth_name}
                                    </p>
                                    {expandedClothes[travelList._id]?.[cloth._id] && (
                                        <img src={cloth.image_url} width="100" alt={cloth.cloth_name} />
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="travel-list-actions">
                            <button className="edit-btn" onClick={() => navigate(`/edit-travelList/${travelList._id}`)}>
                                <i className="fa-solid fa-pencil"></i> <strong>𝐸𝒹𝒾𝓉</strong>
                            </button>
                            <button className="delete-btn" onClick={() => handleDelete(travelList._id)}>
                                <i className="fa-solid fa-trash"></i> <strong>𝒟𝑒𝓁𝑒𝓉𝑒</strong>
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TravelLists;
